import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface AppRow {
  readonly applicationId: string;
  readonly name: string;
  readonly applicationType: string;
  readonly category: string;
  readonly status: string;
  readonly updatedAt: string | null;
}

const TYPES = ['', 'FORM', 'WORKFLOW', 'DASHBOARD', 'PORTAL'];
const STATUSES = ['', 'DRAFT', 'PUBLISHED', 'ARCHIVED'];

/**
 * GR L28.01 — Low-code Applications (`/lcnc/apps`). Catalogue of citizen-built
 * `lcnc_applications` with their type, category and lifecycle status, each
 * opening the builder. Filterable by type and status. Links to the new-app
 * wizard.
 */
export function GrAppsPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<AppRow[]>([]);
  const [applicationType, setApplicationType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationType) params.set('applicationType', applicationType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: AppRow[] }>(`/v1/lcnc/applications${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationType, status]);

  return (
    <main aria-label="lcnc-apps">
      <h1>{tr('gr28.apps.title')}</h1>
      <p><Link aria-label="new-app" to="/lcnc/apps/new">{tr('gr28.apps.new')}</Link></p>
      <div className="filters">
        <label>
          {tr('gr28.apps.type')}
          <select aria-label="filter-type" value={applicationType} onChange={(e) => setApplicationType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('gr28.apps.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('gr28.apps.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('gr28.apps.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('gr28.apps.loading')}</p> : (
        <>
          <p aria-label="app-count">{tr('gr28.apps.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{tr('gr28.apps.empty')}</p> : (
            <table aria-label="app-table">
              <thead><tr><th>{tr('gr28.apps.colName')}</th><th>{tr('gr28.apps.colType')}</th><th>{tr('gr28.apps.colCategory')}</th><th>{tr('gr28.apps.colStatus')}</th><th></th></tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.applicationId}>
                    <td>{a.name}</td><td>{a.applicationType}</td><td>{a.category}</td><td>{a.status}</td>
                    <td><Link aria-label={`open-${a.applicationId}`} to={`/lcnc/apps/${a.applicationId}/builder`}>{tr('gr28.apps.open')}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
